import { WorkspaceService } from '@affine/core/modules/workspace';
import type { DocFrontend } from '@affine/nbstore';
import type {
  DatabaseRefusedChangeActions,
  DatabaseRefusedChangeReason,
  DatabaseRefusedChangeRequest,
} from '@blocksuite/affine/blocks/database';
import { collectDatabaseStorageSyncDocIds } from '@streamlain/database-identity';
import type { FrameworkProvider } from '@toeverything/infra';
import { distinctUntilChanged, map } from 'rxjs';

type RefusedChangeEngine = Pick<
  DocFrontend,
  'state$' | 'listRefusedPublications' | 'resolveRefusedPublications'
>;

/** A publication may hold only one shard, so every document of the database counts. */
export function collectRefusedChangeDocIds(
  request: DatabaseRefusedChangeRequest
): string[] {
  return [
    ...new Set([
      ...request.docIds,
      ...collectDatabaseStorageSyncDocIds({
        databaseId: request.databaseId,
        workspaceId: request.workspaceId,
      }),
    ]),
  ];
}

/**
 * Reads the stored refusal when the wait starts and whenever a document
 * enters or leaves its sync isolation; a refused publication isolates its
 * documents, so nothing is asked on an interval.
 */
export function watchRefusedChanges(
  engine: RefusedChangeEngine,
  getRequest: () => DatabaseRefusedChangeRequest | null,
  onChange: (reason: DatabaseRefusedChangeReason | null) => void
): () => void {
  let stopped = false;
  let latest = 0;
  const read = () => {
    const request = getRequest();
    const current = ++latest;
    const report = (reason: DatabaseRefusedChangeReason | null) => {
      if (!stopped && current === latest) onChange(reason);
    };
    if (!request) {
      report(null);
      return;
    }
    engine.listRefusedPublications(collectRefusedChangeDocIds(request)).then(
      refused => report(refused[0]?.refusal ?? null),
      () => report(null)
    );
  };
  const subscription = engine.state$
    .pipe(
      map(state =>
        state.syncIsolatedFailures
          .map(failure => `${failure.docId}:${failure.firstSeenAt}`)
          .sort()
          .join('|')
      ),
      distinctUntilChanged()
    )
    .subscribe(read);
  return () => {
    stopped = true;
    subscription.unsubscribe();
  };
}

export function createDatabaseRefusedChangeActions(
  framework: FrameworkProvider
): DatabaseRefusedChangeActions {
  const engine = () => framework.get(WorkspaceService).workspace.engine.doc;

  return {
    watch: (getRequest, onChange) =>
      watchRefusedChanges(engine(), getRequest, onChange),
    resend: async request => {
      await engine().resolveRefusedPublications(
        collectRefusedChangeDocIds(request),
        'retry'
      );
    },
    discard: async request => {
      await engine().resolveRefusedPublications(
        collectRefusedChangeDocIds(request),
        'discard'
      );
      // The open copies are detached; the reload pulls the server state.
      window.location.reload();
    },
  };
}
